import { cn } from '@core/utils';
import { NodeSelection } from 'prosemirror-state';
import type { ReactNode } from 'react';
import { useContext, useState } from 'react';

import { Button } from '../../atom/Button/Button';
import { Input } from '../../atom/Input/Input';
import { InputDecorator } from '../../atom/InputDecorator/InputDecorator';
import { Modal } from '../../atom/Modal/Modal';
import { Radio } from '../../atom/Radio/Radio';
import { CustomEditorContext } from '.';
import BoxedTextIcon from './_assets/boxed-text.svg';
import HrIcon from './_assets/hr.svg';
import type { ImageButtonProps } from './_components/ImageButton';
import { ImageButton } from './_components/ImageButton';
import { TEXT_ALIGN } from './_constants';
import {
  CLASS_TO_BUTTON_SIZE,
  CLASS_TO_BUTTON_THEME,
  type CustomLinkOptions,
} from './_utils/CustomLink';
import { FONT_SIZES, type FontSizeType } from './_utils/FontSize';
import { FONT_WEIGHTS } from './_utils/FontWeight';
import { HIGHLIGHTS } from './_utils/Highlight';
import { TEXT_COLORS } from './_utils/TextColor';

export interface ToolbarProps {
  imageUploadOptions?: ImageButtonProps;
  children?: ReactNode;
}

type Level = 2 | 3 | 4 | 5 | 6;
const HEADING_OPTIONS = [
  { value: 'paragraph', label: '본문' },
  { value: 'h2', label: '제목 2' },
  { value: 'h3', label: '제목 3' },
  { value: 'h4', label: '제목 4' },
  { value: 'h5', label: '제목 5' },
  { value: 'h6', label: '제목 6' },
];

export const Toolbar = ({ imageUploadOptions, children }: ToolbarProps) => {
  const { editor } = useContext(CustomEditorContext);

  const [isLinkModalOpened, setIsLinkModalOpened] = useState(false);

  const isImageSelected =
    editor?.state.selection instanceof NodeSelection &&
    editor?.state.selection.node.type.name === 'image';

  const [customLinkOptions, setCustomLinkOptions] = useState<CustomLinkOptions>(
    {
      theme: 'text',
      size: 'H56',
      isOutLink: false,
      href: '',
    },
  );

  const handleLinkButtonClick = () => {
    if (isImageSelected) {
      const imageAttrs =
        editor?.state.selection instanceof NodeSelection
          ? editor.state.selection.node.attrs
          : null;
      setCustomLinkOptions((prev) => ({
        ...prev,
        theme: 'image',
        href: imageAttrs?.href || '',
        isOutLink: imageAttrs?.target === '_blank',
      }));
    } else if (editor?.isActive('link')) {
      const linkAttrs = editor.getAttributes('link');
      const linkClass = linkAttrs.class || '';

      const theme =
        (Object.entries(CLASS_TO_BUTTON_THEME).find(([cls]) =>
          linkClass.includes(cls),
        )?.[1] as CustomLinkOptions['theme']) ?? 'text';

      const size =
        (Object.entries(CLASS_TO_BUTTON_SIZE).find(([cls]) =>
          linkClass.includes(cls),
        )?.[1] as CustomLinkOptions['size']) ?? 'H56';

      setCustomLinkOptions({
        theme,
        size,
        href: linkAttrs.href || '',
        isOutLink: linkAttrs.target === '_blank',
      });
    } else {
      setCustomLinkOptions({
        theme: 'text',
        size: 'H56',
        href: '',
        isOutLink: false,
      });
    }
    setIsLinkModalOpened(true);
  };

  if (!editor) {
    return null;
  }

  const getSelectedHeading = () => {
    for (let i = 2; i <= 6; i++) {
      if (editor.isActive('heading', { level: i })) {
        return `h${i}`;
      }
    }

    return 'paragraph';
  };

  const selectedHeading = getSelectedHeading();

  const [selectedColor] =
    TEXT_COLORS.find(([, color]) => editor.isActive('textStyle', { color })) ??
    [];

  const selectedFontWeight = FONT_WEIGHTS.find((fontWeight) =>
    editor.isActive('fontWeight', { fontWeight }),
  );

  const selectedFontSize = FONT_SIZES.find((fontSize) => {
    return editor.isActive('fontSize', { fontSize });
  });

  const isBoxedText = editor.isActive('boxedText');

  const selectedTextAlign = TEXT_ALIGN.find((textAlign) =>
    editor.isActive('textAlign', { textAlign }),
  );

  return (
    <nav className="flex flex-wrap items-center justify-center gap-8 bg-Gray-100 p-8">
      <p className="text-Gray-800 typo-caption1">*h1 태그는 제목 고정입니다.</p>

      <select
        value={selectedHeading}
        onChange={(e) => {
          const value = e.target.value;
          if (value === 'paragraph') {
            editor.chain().focus().setParagraph().run();
          } else {
            const level = Number.parseInt(value.substring(1)) as Level;
            editor.chain().focus().setHeading({ level }).run();
          }
        }}
      >
        {HEADING_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      <select
        value={selectedFontSize}
        onChange={(e) => {
          editor
            .chain()
            .focus()
            .setFontSize(e.target.value as FontSizeType)
            .run();
        }}
      >
        {FONT_SIZES.map((fontSize) => (
          <option key={fontSize} className={fontSize} value={fontSize}>
            {fontSize}
          </option>
        ))}
      </select>
      <select
        value={selectedTextAlign}
        onChange={(e) => {
          editor?.chain().focus().setTextAlign(e.target.value).run();
        }}
      >
        {TEXT_ALIGN.map((textAlign) => (
          <option key={textAlign} value={textAlign}>
            {textAlign}
          </option>
        ))}
      </select>

      <section className="flex gap-8 rounded-xsmall border-1 border-Gray-600 px-8 py-2">
        {TEXT_COLORS.map(([label, color]) => (
          <button
            key={label}
            className={cn('font-800', {
              underline: selectedColor === label,
            })}
            style={{
              color,
            }}
            type="button"
            onClick={() => editor?.chain().focus().setColor(color).run()}
          >
            A
          </button>
        ))}
      </section>
      <section className="flex gap-8 rounded-xsmall border-1 border-Gray-600 px-8 py-2">
        {FONT_WEIGHTS.map((fontWeight) => (
          <button
            key={fontWeight}
            className={cn(fontWeight, {
              underline: selectedFontWeight === fontWeight,
            })}
            type="button"
            onClick={() =>
              editor?.chain().focus().setFontWeight(fontWeight).run()
            }
          >
            B
          </button>
        ))}
      </section>
      {HIGHLIGHTS.map((highlight) => {
        const isActive = editor.isActive('highlight', { highlight });

        return (
          <button
            key={highlight}
            className={cn(
              highlight,
              'flex size-24 min-h-24 min-w-24 items-center justify-center rounded-xsmall font-700',
              {
                'border-1 border-Gray-800': isActive,
              },
            )}
            type="button"
            onClick={() =>
              isActive
                ? editor.chain().focus().unsetHighlight().run()
                : editor.chain().focus().setHighlight(highlight).run()
            }
          />
        );
      })}
      <button
        className={cn('box-border flex size-24 items-center justify-center', {
          'border-Gray-800 border-1 rounded-xxsmall': isBoxedText,
        })}
        type="button"
        onClick={() =>
          isBoxedText
            ? editor.chain().focus().unsetBoxedText().run()
            : editor.chain().focus().setBoxedText().run()
        }
      >
        <BoxedTextIcon />
      </button>
      <button
        type="button"
        onClick={() => {
          editor?.chain().focus().setHorizontalRule().run();
        }}
      >
        <HrIcon />
      </button>
      <ImageButton {...imageUploadOptions} />
      <Button
        dimensions="H26"
        roundness="rectangle"
        onClick={handleLinkButtonClick}
      >
        링크
      </Button>
      <Modal
        opened={isLinkModalOpened}
        onClose={() => setIsLinkModalOpened(false)}
      >
        <article className="mb-32 flex flex-col gap-16">
          <InputDecorator label="주소">
            <Input
              value={customLinkOptions.href}
              onChange={(e) => {
                setCustomLinkOptions((prev) => ({
                  ...prev,
                  href: e.target.value,
                }));
              }}
            />
          </InputDecorator>
          <InputDecorator label="링크 형식">
            <Radio.Group
              value={customLinkOptions.theme}
              onChange={(value) => {
                setCustomLinkOptions((prev) => ({
                  ...prev,
                  theme: value,
                }));
              }}
            >
              <Radio.Item value="text">문자</Radio.Item>
              <Radio.Item value="primary-fill-button">버튼 1</Radio.Item>
              <Radio.Item value="sub-fill-button">버튼 2</Radio.Item>
              <Radio.Item value="image">이미지</Radio.Item>
            </Radio.Group>
          </InputDecorator>
          <InputDecorator label="창 열기">
            <Radio.Group
              value={customLinkOptions.isOutLink ? 'out' : 'in'}
              onChange={(value) => {
                setCustomLinkOptions((prev) => ({
                  ...prev,
                  isOutLink: value === 'out',
                }));
              }}
            >
              <Radio.Item value="out">외부에서 열기</Radio.Item>
              <Radio.Item value="in">내부에서 열기</Radio.Item>
            </Radio.Group>
          </InputDecorator>
        </article>
        <footer className="flex w-full gap-8">
          <Button
            className="flex-1"
            disabled={!customLinkOptions.href}
            onClick={() => {
              setIsLinkModalOpened(false);
              editor.chain().focus().setCustomLink(customLinkOptions).run();
            }}
          >
            설정
          </Button>
          <Button
            className="flex-1"
            theme="sub-fill"
            onClick={() => {
              setIsLinkModalOpened(false);
            }}
          >
            취소
          </Button>
        </footer>
      </Modal>
      {children}
    </nav>
  );
};
